import * as React from "react";
import ExpenseDataService from "../services/Expense.services";
import PerDiemDataService from "../services/PerDiem.services";
import PropTypes from "prop-types";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import ExpenseCard from "../components/ExpenseCard";
import MileCard from "./MileCard";
import PerDiemCard from "./PerDiemCard";
import ExpenseSummary from "../components/ExpenseSummary";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import AirplanemodeActiveIcon from "@mui/icons-material/AirplanemodeActive";
import AddExpenseForm from "../components/AddExpenseForm";
import AddMileForm from "./AddMileForm";
import AddPerDiemForm from "./AddPerDiemForm";
import Button from "@mui/material/Button";
import { useLoading } from "../contexts/loading-context";
import { pink } from "@mui/material/colors";
import { blue } from "@mui/material/colors";
import { orange } from "@mui/material/colors";
import { green } from "@mui/material/colors";
import moment from "moment";

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 0, pt: 3, mb: 0, pb: 0 }}>{children}</Box>
            )}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

export default function ExpaneseTabs(props) {
    const [value, setValue] = React.useState(0);
    const [expenses, setExpenses] = React.useState([]);
    const [miles, setMiles] = React.useState([]);
    const [totalCreditCard, setTotalCreditCard] = React.useState(0);
    const [approvedCreditCard, setApprovedCreditCard] = React.useState(0);
    const [totalReimbursable, setTotalReimbursable] = React.useState(0);
    const [balanceReimbursable, setBalanceReimbursable] = React.useState(0);
    const [totalMiles, setTotalMiles] = React.useState(0);
    const [balanceMiles, setBalanceMiles] = React.useState(0);
    const [perDiems, setPerDiems] = React.useState([]);
    const [totalPerDiem, setTotalPerDiem] = React.useState(0);
    const [approvedPerDiem, setApprovedPerDiem] = React.useState(0);
    const [balancePerDiem, setBalancePerDiem] = React.useState(0);
    const [
        awaitingApprovalPerDiem,
        setAwaitingApprovalPerDiem,
    ] = React.useState(0);
    const [paidPerDiem, setPaidPerDiem] = React.useState(0);
    // Track latest requests to avoid out-of-order responses overriding state
    const expensesReqIdRef = React.useRef(0);
    const milesReqIdRef = React.useRef(0);
    const {
        darkState,
        month,
        perDiemIsoWeek,
        user,
        employee,
        employees,
        token,
        handleOpenSnackbar,
        open,
        setOpen,
        handleChangeEmployee,
        isAdmin,
        onIsoWeekChange,
    } = props;
    const { openMiles, setOpenMiles } = props;
    const { openPerDiem = false, setOpenPerDiem = () => {} } = props;
    const { perDiemRates = [] } = props;
    const { editing, setEditing, setTabIndex } = props;
    const [editExpense, setEditExpense] = React.useState({});
    const { setIsLoading } = useLoading();
    const inFlightRef = React.useRef(0);
    const startLoading = React.useCallback(() => {
        inFlightRef.current += 1;
        setIsLoading(true);
    }, [setIsLoading]);
    const endLoading = React.useCallback(() => {
        inFlightRef.current = Math.max(0, inFlightRef.current - 1);
        if (inFlightRef.current === 0) setIsLoading(false);
    }, [setIsLoading]);

    React.useEffect(() => {
        if (!token) return;
        retrieveExpenses();
        retrieveMiles();
    }, [month, employee, token]);

    React.useEffect(() => {
        if (!token) return;
        retrievePerDiems();
    }, [perDiemIsoWeek, employee, token]);

    React.useEffect(() => {
        setTabIndex(value);
    }, [value]);

    // Remove duplicate fetch/useEffect patterns to avoid racing

    const hasSelectedEmployee = Boolean(
        isAdmin && employee && employee.id != null
    );

    // For admin users, use current user as default if no employee is selected
    const effectiveEmployee = isAdmin && employee ? employee : user;

    const retrieveExpenses = () => {
        startLoading();
        const reqId = ++expensesReqIdRef.current;
        ExpenseDataService.getAll(token, month)
            .then((response) => {
                // Ignore out-of-date responses
                if (reqId !== expensesReqIdRef.current) return;
                const all = response.data || [];
                const list = isAdmin
                    ? all.filter(
                          (obj) =>
                              obj.user && obj.user.id === effectiveEmployee.id
                      )
                    : all;

                setExpenses(list);

                // Compute totals once to avoid setState thrashing
                const totals = list.reduce(
                    (acc, item) => {
                        const price = item.price || 0;
                        if (item.is_reimbursable) {
                            acc.reimbursable += price;
                            if (!item.is_approved) {
                                acc.balanceReimbursable += price;
                            }
                        } else {
                            acc.credit += price;
                            if (item.is_approved) {
                                acc.approvedCredit += price;
                            }
                        }
                        return acc;
                    },
                    {
                        credit: 0,
                        reimbursable: 0,
                        balanceReimbursable: 0,
                        approvedCredit: 0,
                    }
                );
                setTotalReimbursable(totals.reimbursable);
                setBalanceReimbursable(totals.balanceReimbursable);
                setTotalCreditCard(totals.credit);
                setApprovedCreditCard(totals.approvedCredit);
            })
            .catch((e) => {
                console.log(e);
                handleOpenSnackbar(
                    "error",
                    "Something Went Wrong!! Please try again."
                );
            })
            .finally(() => {
                // Always end loading for this request; state updates above are guarded
                endLoading();
            });
    };

    const perDiemSummaryInfo = React.useMemo(() => {
        if (!perDiemIsoWeek) {
            return { heading: "", subheading: "" };
        }
        const isoMoment = moment(perDiemIsoWeek, "GGGG[W]WW", true);
        if (!isoMoment.isValid()) {
            return { heading: perDiemIsoWeek, subheading: "" };
        }
        const start = isoMoment.clone().startOf("isoWeek");
        const end = start.clone().add(6, "days");
        return {
            heading: `${isoMoment.format("GGGG[W]WW")}`,
            subheading: `${start.format("MMM D")} - ${end.format("MMM D")}`,
        };
    }, [perDiemIsoWeek]);

    const createExpense = (data) => {
        startLoading();
        const userId = Number(effectiveEmployee.id);

        ExpenseDataService.createExpense(data, token, userId)
            .then((response) => {
                window.scrollTo(0, 0);
                handleOpenSnackbar(
                    "success",
                    "Your expense has been submitted for approval"
                );
                retrieveExpenses();
            })
            .catch((e) => {
                console.log(e);
                handleOpenSnackbar(
                    "error",
                    "Something Went Wrong!! Please try again."
                );
            })
            .finally(() => {
                endLoading();
            });
    };

    const deleteExpense = (expenseId) => {
        ExpenseDataService.deleteExpense(expenseId, props.token)
            .then((response) => {
                window.scrollTo(0, 0);
                handleOpenSnackbar("warning", "Your expense has been deleted");
                retrieveExpenses();
            })
            .catch((e) => {
                console.log(e);
                handleOpenSnackbar(
                    "error",
                    "Something Went Wrong!! Please try again."
                );
            });
    };

    const updateExpense = (expenseId, data) => {
        startLoading();
        ExpenseDataService.updateExpense(expenseId, data, token)
            .then((response) => {
                window.scrollTo(0, 0);
                handleOpenSnackbar(
                    "info",
                    "Your expense has been submitted for approval"
                );
                retrieveExpenses();
            })
            .catch((e) => {
                console.log(e);
                handleOpenSnackbar(
                    "error",
                    "Something Went Wrong!! Please try again."
                );
            })
            .finally(() => {
                endLoading();
            });
    };

    const approveExpense = (expenseId) => {
        ExpenseDataService.approveExpense(expenseId, token)
            .then((response) => {
                retrieveExpenses();
            })
            .catch((e) => {
                console.log(e);
                handleOpenSnackbar(
                    "error",
                    "Something Went Wrong!! Please try again."
                );
            });
    };

    const retrieveMiles = () => {
        startLoading();
        const reqId = ++milesReqIdRef.current;
        ExpenseDataService.getAllMiles(token, month)
            .then((response) => {
                // Ignore out-of-date responses
                if (reqId !== milesReqIdRef.current) return;
                const all = response.data || [];
                const list = isAdmin
                    ? all.filter(
                          (obj) =>
                              obj.user && obj.user.id === effectiveEmployee.id
                      )
                    : all;
                setMiles(list);
                const totals = list.reduce(
                    (acc, item) => {
                        const price = item.price || 0;
                        acc.total += price;
                        if (!item.is_approved) {
                            acc.balance += price;
                        }
                        return acc;
                    },
                    { total: 0, balance: 0 }
                );
                setTotalMiles(totals.total);
                setBalanceMiles(totals.balance);
            })
            .catch((e) => {
                console.log(e);
                handleOpenSnackbar(
                    "error",
                    "Something Went Wrong!! Please try again."
                );
            })
            .finally(() => {
                // Always end loading for this request; state updates above are guarded
                endLoading();
            });
    };

    const approveMile = (expenseId) => {
        ExpenseDataService.approveMile(expenseId, token)
            .then((response) => {
                retrieveMiles();
            })
            .catch((e) => {
                console.log(e);
                handleOpenSnackbar(
                    "error",
                    "Something Went Wrong!! Please try again."
                );
            });
    };

    const deleteMile = (expenseId) => {
        ExpenseDataService.deleteMile(expenseId, props.token)
            .then((response) => {
                window.scrollTo(0, 0);
                handleOpenSnackbar("warning", "Your miles have been deleted");
                retrieveMiles();
            })
            .catch((e) => {
                console.log(e);
                handleOpenSnackbar(
                    "error",
                    "Something Went Wrong!! Please try again."
                );
            });
    };

    const createMiles = (data) => {
        const userId = Number(effectiveEmployee.id);

        ExpenseDataService.createMile(data, token, userId)
            .then((response) => {
                window.scrollTo(0, 0);
                handleOpenSnackbar(
                    "success",
                    "Your expense has been submitted for approval"
                );
                retrieveMiles();
            })
            .catch((e) => {
                console.log(e);
                handleOpenSnackbar(
                    "error",
                    "Something Went Wrong!! Please try again."
                );
            });
    };

    const updateMiles = (
        expenseId,
        data,
        newUserId = null,
        employeeChanged = false
    ) => {
        if (employeeChanged && newUserId) {
            // Employee has changed - delete old entry and create new one
            ExpenseDataService.deleteMile(expenseId, token)
                .then((response) => {
                    // After successful deletion, create new miles entry with new user
                    return ExpenseDataService.createMile(
                        data,
                        token,
                        newUserId
                    );
                })
                .then((response) => {
                    window.scrollTo(0, 0);
                    handleOpenSnackbar(
                        "info",
                        "Miles entry has been updated with new employee assignment"
                    );
                    retrieveMiles();
                })
                .catch((e) => {
                    console.log(e);
                    handleOpenSnackbar(
                        "error",
                        "Something went wrong updating the miles entry! Please try again."
                    );
                });
        } else {
            // Regular update - employee hasn't changed
            ExpenseDataService.updateMile(expenseId, data, token)
                .then((response) => {
                    window.scrollTo(0, 0);
                    handleOpenSnackbar(
                        "info",
                        "Your expense has been submitted for approval"
                    );
                    retrieveMiles();
                })
                .catch((e) => {
                    console.log(e);
                    handleOpenSnackbar(
                        "error",
                        "Something Went Wrong!! Please try again."
                    );
                });
        }
    };

    const retrievePerDiems = () => {
        if (!perDiemIsoWeek) {
            setPerDiems([]);
            setTotalPerDiem(0);
            setBalancePerDiem(0);
            setApprovedPerDiem(0);
            setAwaitingApprovalPerDiem(0);
            setPaidPerDiem(0);
            return;
        }

        const currentUserId = effectiveEmployee.id;

        startLoading();

        const apiCall =
            isAdmin && hasSelectedEmployee
                ? PerDiemDataService.getAdminWeek(
                      token,
                      perDiemIsoWeek,
                      currentUserId
                  )
                : PerDiemDataService.getWeek(token, perDiemIsoWeek);

        // console.log("Per Diem API Call Details:", {
        //     isAdmin,
        //     hasSelectedEmployee,
        //     currentUserId,
        //     perDiemIsoWeek,
        //     apiType: isAdmin && hasSelectedEmployee ? "admin" : "regular",
        // });

        apiCall
            .then((response) => {
                const all = response.data || [];
                const perDiems = isAdmin
                    ? all.filter(
                          (obj) =>
                              obj.user && obj.user.id === effectiveEmployee.id
                      )
                    : all;
                setPerDiems(perDiems);

                // Calculate totals
                const totals = perDiems.reduce(
                    (acc, perDiem) => {
                        const startDate = moment(perDiem.start_date);
                        const endDate = moment(perDiem.end_date);
                        const nights = Math.max(
                            endDate.diff(startDate, "days"),
                            0
                        );
                        const rateAmount =
                            perDiem.rate && perDiem.rate.amount
                                ? perDiem.rate.amount
                                : 45.0;
                        const amount = nights * rateAmount;

                        acc.total += amount;

                        if (perDiem.is_approved) {
                            acc.approved += amount;
                            if (perDiem.is_paid) {
                                acc.paid += amount;
                            } else {
                                acc.pendingPayment += amount;
                            }
                        } else {
                            acc.awaitingApproval += amount;
                        }

                        return acc;
                    },
                    {
                        total: 0,
                        approved: 0,
                        pendingPayment: 0,
                        awaitingApproval: 0,
                        paid: 0,
                    }
                );

                setTotalPerDiem(totals.total);
                setApprovedPerDiem(totals.approved);
                setBalancePerDiem(totals.pendingPayment);
                setAwaitingApprovalPerDiem(totals.awaitingApproval);
                setPaidPerDiem(totals.paid);
            })
            .catch((e) => {
                console.log(e);
                handleOpenSnackbar(
                    "error",
                    "Something went wrong fetching per diems! Please try again."
                );
            })
            .finally(() => {
                endLoading();
            });
    };

    const approvePerDiem = (perDiemId) => {
        PerDiemDataService.approvePerDiem(perDiemId, token)
            .then((response) => {
                handleOpenSnackbar(
                    "success",
                    "Per diem has been approved successfully."
                );
                retrievePerDiems();
            })
            .catch((e) => {
                console.log(e);
                handleOpenSnackbar(
                    "error",
                    "Something went wrong approving the per diem! Please try again."
                );
            });
    };

    const deletePerDiem = (perDiemId) => {
        PerDiemDataService.deletePerDiem(perDiemId, token)
            .then((response) => {
                window.scrollTo(0, 0);
                handleOpenSnackbar("warning", "Your per diem has been deleted");
                retrievePerDiems();
            })
            .catch((e) => {
                console.log(e);
                handleOpenSnackbar(
                    "error",
                    "Something went wrong deleting the per diem! Please try again."
                );
            });
    };

    const createPerDiem = React.useCallback(
        (data) => {
            startLoading();
            const userId = Number(effectiveEmployee.id);

            // console.log(
            //     "Sending PerDiem API request with userId:",
            //     userId,
            //     "data:",
            //     JSON.stringify(data, null, 2)
            // );

            PerDiemDataService.createPerDiem(data, token, userId)
                .then((response) => {
                    // console.log(
                    //     "PerDiem creation successful:",
                    //     JSON.stringify(response.data, null, 2)
                    // );
                    window.scrollTo(0, 0);
                    handleOpenSnackbar(
                        "success",
                        "Your per diem has been submitted for approval"
                    );
                    retrievePerDiems();
                })
                .catch((e) => {
                    console.error("PerDiem creation failed:", e);
                    console.error(
                        "Error response:",
                        e.response && e.response.data
                    );
                    console.error(
                        "Error status:",
                        e.response && e.response.status
                    );
                    handleOpenSnackbar(
                        "error",
                        "Something went wrong creating the per diem! Please try again."
                    );
                })
                .finally(() => {
                    endLoading();
                });
        },
        [handleOpenSnackbar, effectiveEmployee, token, startLoading, endLoading]
    );

    const updatePerDiem = React.useCallback(
        (perDiemId, data) => {
            startLoading();
            PerDiemDataService.updatePerDiem(perDiemId, data, token)
                .then((response) => {
                    window.scrollTo(0, 0);
                    handleOpenSnackbar(
                        "info",
                        "Your per diem has been updated successfully"
                    );
                    retrievePerDiems();
                })
                .catch((e) => {
                    console.log(e);
                    handleOpenSnackbar(
                        "error",
                        "Something went wrong updating the per diem! Please try again."
                    );
                })
                .finally(() => {
                    endLoading();
                });
        },
        [handleOpenSnackbar, token, startLoading, endLoading]
    );

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    return (
        <Box
            sx={{
                width: "100%",
                maxWidth: 500,
                mx: "auto",
                px: { xs: 0, sm: 0 },
            }}
        >
            <Box
                sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    overflowX: "auto",
                }}
            >
                <Tabs
                    value={value}
                    onChange={handleChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{
                        px: { xs: 0, sm: 0 },
                        "& .MuiTab-root": {
                            minWidth: { xs: "auto", sm: 140 },
                            px: { xs: 1.5, sm: 2 },
                        },
                        "& .MuiTab-root.Mui-selected": {
                            color:
                                value === 0
                                    ? green[500]
                                    : value === 1
                                    ? pink[500]
                                    : value === 2
                                    ? blue[500]
                                    : orange[500],
                        },
                        "& .MuiTabs-indicator": {
                            backgroundColor:
                                value === 0
                                    ? green[500]
                                    : value === 1
                                    ? pink[500]
                                    : value === 2
                                    ? blue[500]
                                    : orange[500],
                        },
                    }}
                >
                    <Tab
                        // sx={{ width: "50%" }}
                        icon={<CreditCardIcon />}
                        label="Company Card"
                    />
                    <Tab
                        // sx={{ width: "50%" }}
                        icon={<PaidOutlinedIcon />}
                        label="Reimbursable"
                    />
                    <Tab
                        // sx={{ width: "50%" }}
                        icon={<DirectionsCarFilledOutlinedIcon />}
                        label="Miles"
                    />
                    <Tab
                        // sx={{ width: "50%" }}
                        icon={<AirplanemodeActiveIcon />}
                        label="Per Diem"
                    />
                </Tabs>
            </Box>
            <TabPanel value={value} index={0}>
                <ExpenseSummary
                    darkState={darkState}
                    month={month}
                    amount={totalCreditCard}
                    approvedAmount={approvedCreditCard}
                    title={"Company Card"}
                />
                {expenses
                    ? expenses.map((expense) =>
                          !expense.is_reimbursable ? (
                              <ExpenseCard
                                  darkState={darkState}
                                  key={expense.id}
                                  setOpen={setOpen}
                                  setEditing={setEditing}
                                  setEditExpense={setEditExpense}
                                  expense={expense}
                                  user={user}
                                  deleteExpense={deleteExpense}
                                  approveExpense={approveExpense}
                                  isAdmin={isAdmin}
                              />
                          ) : (
                              ""
                          )
                      )
                    : ""}
            </TabPanel>
            <TabPanel value={value} index={1}>
                <ExpenseSummary
                    darkState={darkState}
                    month={month}
                    amount={totalReimbursable}
                    balanceAmount={balanceReimbursable}
                    title={"Reimbursable"}
                />
                {expenses
                    ? expenses.map((expense) =>
                          expense.is_reimbursable ? (
                              <ExpenseCard
                                  darkState={darkState}
                                  key={expense.id}
                                  setOpen={setOpen}
                                  setEditing={setEditing}
                                  setEditExpense={setEditExpense}
                                  expense={expense}
                                  user={user}
                                  deleteExpense={deleteExpense}
                                  approveExpense={approveExpense}
                                  isAdmin={isAdmin}
                              />
                          ) : (
                              ""
                          )
                      )
                    : ""}
            </TabPanel>
            <TabPanel value={value} index={2}>
                <ExpenseSummary
                    darkState={darkState}
                    month={month}
                    amount={totalMiles}
                    balanceAmount={balanceMiles}
                    title={"Miles"}
                />
                {miles
                    ? miles.map((mile) => (
                          <MileCard
                              darkState={darkState}
                              key={mile.id}
                              expense={mile}
                              setOpenMiles={setOpenMiles}
                              setEditing={setEditing}
                              setEditExpense={setEditExpense}
                              user={user}
                              deleteMile={deleteMile}
                              approveMile={approveMile}
                          />
                      ))
                    : ""}
            </TabPanel>
            <TabPanel value={value} index={3}>
                <ExpenseSummary
                    darkState={darkState}
                    month={month}
                    amount={totalPerDiem}
                    balanceAmount={balancePerDiem}
                    approvedAmount={awaitingApprovalPerDiem}
                    approvedLabel="Pending Approval"
                    periodHeading={perDiemSummaryInfo.heading}
                    periodSubheading={perDiemSummaryInfo.subheading}
                    title={"Per Diem"}
                />
                {perDiems
                    ? perDiems.map((perDiem) => (
                          <PerDiemCard
                              darkState={darkState}
                              key={perDiem.id}
                              perDiem={perDiem}
                              setOpenPerDiem={setOpenPerDiem}
                              setEditing={setEditing}
                              setEditPerDiem={setEditExpense}
                              user={user}
                              deletePerDiem={deletePerDiem}
                              approvePerDiem={approvePerDiem}
                              isAdmin={isAdmin}
                          />
                      ))
                    : ""}
            </TabPanel>
            <AddExpenseForm
                currentTabIndex={value}
                employees={employees}
                open={open}
                expense={editExpense}
                setOpen={setOpen}
                editing={editing}
                setEditing={setEditing}
                user={user}
                token={token}
                employee={employee}
                handleChangeEmployee={handleChangeEmployee}
                handleOpenSnackbar={handleOpenSnackbar}
                createExpense={createExpense}
                updateExpense={updateExpense}
            />
            <AddMileForm
                employees={employees}
                user={user}
                token={token}
                editing={editing}
                expense={editExpense}
                employee={employee}
                openMiles={openMiles}
                setOpenMiles={setOpenMiles}
                handleChangeEmployee={handleChangeEmployee}
                handleOpenSnackbar={handleOpenSnackbar}
                createMiles={createMiles}
                updateMiles={updateMiles}
            />
            <AddPerDiemForm
                employees={employees}
                user={user}
                token={token}
                editing={editing}
                expense={editExpense}
                isoWeek={perDiemIsoWeek}
                onIsoWeekChange={onIsoWeekChange}
                employee={employee}
                openPerDiem={openPerDiem}
                setOpenPerDiem={setOpenPerDiem}
                handleChangeEmployee={handleChangeEmployee}
                perDiemRates={perDiemRates}
                createPerDiem={createPerDiem}
                updatePerDiem={updatePerDiem}
            />
            {/* Global loading handled by LoadingContext Backdrop */}
        </Box>
    );
}
